import { FileCheck2, ShieldCheck, Upload } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { StatusNotice } from '../components/StatusNotice';
import { FormSelectMenu } from '../components/ui/FormSelectMenu';
import { api } from '../lib/api';
import { WorkspaceTabs } from './AgencyHostelsPage';

const documentOptions = [
  { value: 'ghana_card', label: 'Ghana Card or identity document', description: 'Verify the responsible person' },
  { value: 'business_registration', label: 'Business registration', description: 'Verify the registered business' },
  { value: 'proof_of_address', label: 'Proof of business address', description: 'Confirm the agency location' },
  { value: 'other', label: 'Other supporting document', description: 'Submit another relevant record' },
];

export function AgencyVerificationPage() {
  const [docs, setDocs] = useState<Array<Record<string, any>>>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try { setDocs(await api.get('/api/agency/documents')); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not load documents.'); }
  }
  useEffect(() => { void load(); }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Choose a document.'); return; }
    setBusy(true); setError('');
    try {
      const uploaded = await api.upload(file, 'agency_document');
      await api.post('/api/agency/documents', { documentType: form.get('documentType'), objectKey: uploaded.objectKey, originalFilename: file.name });
      setNotice('Document uploaded privately and submitted for review.');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not submit document.'); }
    finally { setBusy(false); }
  }

  return <div className="page">
    <PageHeader eyebrow="Agency workspace" title="Business Pass verification" description="Documents are private. Public profiles show only the resulting verification tier and status." />
    <WorkspaceTabs active="verification" />
    {error && <StatusNotice type="error">{error}</StatusNotice>}{notice && <StatusNotice type="success">{notice}</StatusNotice>}
    <div className="dashboard-grid">
      <section className="surface-section"><div className="feature-icon"><ShieldCheck /></div><h2>Verification levels</h2><div className="process-list"><span><b>01</b><div><strong>Identity verified</strong><small>Government-issued ID approved</small></div></span><span><b>02</b><div><strong>Business verified</strong><small>Business registration reviewed</small></div></span><span><b>03</b><div><strong>Trusted agency</strong><small>Earned later through verified platform performance</small></div></span></div></section>
      <form className="surface-section" onSubmit={submit}><h2>Upload a verification document</h2><label><span>Document type</span><FormSelectMenu name="documentType" ariaLabel="Choose verification document type" options={documentOptions} /></label><label><span>Document file</span><input ref={fileRef} type="file" accept="image/png,image/jpeg,application/pdf" required /></label><button className="primary-button" disabled={busy}><Upload size={17} />{busy ? 'Uploading…' : 'Submit securely'}</button></form>
    </div>
    <section className="surface-section"><div className="section-heading compact"><div><span className="eyebrow">Submitted documents</span><h2>Review history</h2></div></div>{docs.length ? <div className="table-list"><div className="table-row table-head"><span>Document</span><span>File</span><span>Status</span><span>Submitted</span><span>Review note</span></div>{docs.map(document => <div className="table-row" key={String(document.id)}><span><FileCheck2 size={16} />{String(document.document_type).replaceAll('_', ' ')}</span><span>{String(document.original_filename)}</span><span><b className="status-dot" />{String(document.status)}</span><span>{new Date(String(document.created_at)).toLocaleDateString()}</span><span>{String(document.review_note || '—')}</span></div>)}</div> : <p className="muted">No documents submitted yet.</p>}</section>
  </div>;
}
